/**
 * Copyright © 2026 Adnuntius AS.
 */
import {AdnListHelperService} from '../common/controller/list-helper.service';
import {ChangeDetectorRef, Component, Inject, Input, NgZone} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {NgForm} from '@angular/forms';
import {UIRouter} from '@uirouter/core';
import {AppEventBusService} from '../common/events/app-event-bus.service';

import template from './library-creative-edit-page.component.html';
import {AdminResourceEditPageBase} from '../common/controller/admin-resource-edit-page-base';
import {AdnResourceListService} from '../common/controller/adn-resource-list.service';
import {setupLibraryCreativeEditCtrl} from './library-creative-edit-setup';
import {NavigationHistoryService} from '../../components/util/navigation-history.service';
import {TargetingEditorModalService} from '../targeting/targeting-editor-modal.service';
import {CreativeRetailProductModalService} from '../creative/creative-retail-product-modal.service';
import {TeamResource} from '../../components/api/resources/resource/team.resource';
import {AdvertiserResource} from '../../components/api/resources/resource/advertiser.resource';
import {CreativeResource} from '../../components/api/resources/resource/creative.resource';
import {LibraryCreativeResource} from '../../components/api/resources/resource/library-creative.resource';
import {ProductFeedResource} from '../../components/api/resources/resource/product-feed.resource';
import {AdnRollbarService} from '../../components/util/adn-rollbar.service';
import {AssetService} from '../../components/api/resources/resource/asset/asset.service';
import {CreativeHelper} from '../creative/creative-helper.service';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {LocalUserPermissions} from '../../components/session/local-user-permissions';
import {UserProfileSaverService} from '../../components/session/user-profile-saver.service';

import {compareById} from '../common/util/object-id-comparison';

@Component({
  standalone: false,
  selector: 'adn-library-creative-edit-page',
  template
})
export class LibraryCreativeEditPageComponent extends AdminResourceEditPageBase {
  [key: string]: any;

  private _readOnlyDirect: boolean | undefined;
  private _creativeTypeDirect: any;
  private _lineItemInfoDirect: any;
  private setupInitialized = false;
  private activeForm: NgForm | null = null;

  activeTab: 'design' | 'co2' | 'linked' = 'design';
  items: any[] = [];
  /** Snapshot — list-helper replaces items on this host mid-CD (NG0100). */
  displayItems: any[] = [];
  hasItems = false;
  relCreativesCount = 0;
  showingCo2 = false;
  showDesignDetails = false;
  showThirdPartyDetails = false;
  showVariables = false;
  readonly codeMirrorOpts = {lineNumbers: true, mode: 'htmlmixed', smartIndent: false};

  @Input()
  set readOnly(value: boolean) {
    this._readOnlyDirect = value;
  }

  get readOnly(): boolean {
    return this._readOnlyDirect;
  }

  @Input()
  set creativeType(value: any) {
    this._creativeTypeDirect = value;
  }

  get creativeType(): any {
    return this._creativeTypeDirect;
  }

  @Input()
  set lineItemInfo(value: any) {
    this._lineItemInfoDirect = value;
  }

  get lineItemInfo(): any {
    return this._lineItemInfoDirect;
  }

  constructor(
    eventBus: AppEventBusService,
    @Inject(UserProfileSaverService) UserProfileSaver: any,
    @Inject(LocalNetworkProfile) LocalNetworkProfile: any,
    private readonly translate: TranslateService,
    @Inject(TeamResource) private readonly Team: any,
    @Inject(ProductFeedResource) private readonly ProductFeed: any,
    @Inject(CreativeHelper) private readonly CreativeHelper: any,
    private readonly adnListHelper: AdnListHelperService,
    @Inject(AdnRollbarService) private readonly Rollbar: any,
    @Inject(AssetService) private readonly Asset: any,
    @Inject(CreativeResource) private readonly Creative: any,
    @Inject(LibraryCreativeResource) private readonly LibraryCreative: any,
    @Inject(LocalUserPermissions) private readonly LocalUserPermissions: any,
    @Inject(AdvertiserResource) private readonly Advertiser: any,
    private readonly targetingEditorModalService: TargetingEditorModalService,
    private readonly creativeRetailProductModalService: CreativeRetailProductModalService,
    private readonly listService: AdnResourceListService,
    private readonly uiRouter: UIRouter,
    private readonly cdr: ChangeDetectorRef,
    private readonly navigationHistory: NavigationHistoryService,
    ngZone: NgZone
  ) {
    super(eventBus, UserProfileSaver, LocalNetworkProfile, ngZone);
    this.deferFormDirtyTracking();
  }

  onFieldChange(): void {
    this.markExternalFormDirty();
  }

  selectTab(tab: 'design' | 'co2' | 'linked'): void {
    this.activeTab = tab;
    if (tab === 'co2') {
      this.showingCo2 = true;
    }
  }

  readonly compareById = compareById;

  trackById(_index: number, item: any): string {
    return item?.id;
  }

  getTextareaRows(comp: any): number {
    const rawRows = comp.maxLength / 75 + 1;
    if (rawRows < 2) {
      return 1;
    }
    if (rawRows > 10) {
      return 10;
    }
    return Math.floor(rawRows);
  }

  protected afterInitEditingModel(): void {
    this.ensureSetup();
    this._libraryCreativeAfterInit?.();
    this.listService.ensureAngularList(this);
  }

  protected afterInitAfterSave(saveObj?: any): void {
    this._libraryCreativeAfterInitAfterSave?.(saveObj);
    this.listService.ensureAngularList(this);
  }

  onAdvertiserMismatchFixed(saved: any): void {
    if (saved) {
      this.editingModel.advertiser = saved.advertiser;
      this.editingModel.validationWarnings = saved.validationWarnings;
    }
    this._libraryCreativeAfterInitAfterSave?.(saved);
    this.publishModelUpdate();
  }

  protected applySave(resource: any): void {
    super.applySave(resource);
    this._libraryCreativeAfterSave?.(resource);
  }

  protected onBeforeSubmit(): void {
    this._libraryCreativeOnBeforeSubmit?.();
  }

  registerForm(editForm: NgForm): void {
    super.registerForm(editForm);
    this.activeForm = editForm;
  }

  private ensureSetup(): void {
    if (this.setupInitialized || !this.model) {
      return;
    }
    this.setupInitialized = true;

    setupLibraryCreativeEditCtrl(this, {
      model: this.model,
      readOnly: this.readOnly,
      creativeType: this.creativeType,
      lineItemInfo: this.lineItemInfo,
      modelComms: this.modelComms,
      navigationHistory: this.navigationHistory,
      routeParams: this.uiRouter.globals.params,
      translate: this.translate,
      Team: this.Team,
      ProductFeed: this.ProductFeed,
      CreativeHelper: this.CreativeHelper,
      adnListHelper: this.adnListHelper,
      Rollbar: this.Rollbar,
      Asset: this.Asset,
      Creative: this.Creative,
      LibraryCreative: this.LibraryCreative,
      LocalNetworkProfile: this.LocalNetworkProfile,
      LocalUserPermissions: this.LocalUserPermissions,
      Advertiser: this.Advertiser,
      TargetingEditorModalService: this.targetingEditorModalService,
      creativeRetailProductModalService: this.creativeRetailProductModalService,
      cdr: this.cdr
    });
  }
}
